import React from "react";
import { SITE_URL } from "./seo-constants";
import { urlFor } from "./sanity";

type Mark = { _type: string; href?: string };
type Span = { _type: "span"; text: string; marks?: string[] };
type ImageBlock = {
  _type: "image";
  _key?: string;
  asset?: { _ref?: string; url?: string };
  alt?: string;
  caption?: string;
};

// The Webflow importer stamped this placeholder into the alt of every image it
// migrated. It describes nothing, so it must not reach screen readers or
// crawlers — treat it as a missing alt until the affected documents are fixed.
const PLACEHOLDER_ALT = "__wf_reserved_inherit";

// Body images are fetched as raw asset references (the GROQ projections select
// `body`/`content` without dereferencing), so build the URL from the ref rather
// than expecting an `asset.url`. The `url` branch keeps any dereferenced
// projection working.
function imageSrc(img: ImageBlock): string | undefined {
  if (img.asset?._ref) return urlFor(img).width(1200).quality(80).auto("format").url();
  if (img.asset?.url) return `${img.asset.url}?w=1200&q=80&auto=format`;
  return undefined;
}

// Sanity encodes intrinsic dimensions in the asset ref
// (image-<hash>-<width>x<height>-<ext>). Emitting them as width/height reserves
// the right box before the image loads, so it doesn't shift the article's text.
function imageDimensions(ref?: string): { width: number; height: number } | undefined {
  const match = ref?.match(/-(\d+)x(\d+)-[a-z]+$/);
  if (!match) return undefined;
  return { width: Number(match[1]), height: Number(match[2]) };
}
type Block = {
  _type: "block";
  _key?: string;
  style?: string;
  listItem?: "bullet" | "number";
  level?: number;
  children?: Span[];
  markDefs?: Mark[];
};
type TableRow = { _key?: string; cells?: string[] };
type TableBlock = { _type: "table"; _key?: string; rows?: TableRow[] };
type PortableTextNode = Block | ImageBlock | TableBlock | { _type: string; _key?: string };

type Props = {
  value?: unknown;
  className?: string;
};

function resolveSpanText(span: Span, markDefs: Mark[]): React.ReactNode {
  if (!span.marks?.length) return span.text;

  let node: React.ReactNode = span.text;
  const marks = span.marks;

  for (const mark of marks) {
    const def = markDefs.find((d) => (d as { _key?: string })._key === mark || d._type === mark);
    if (def?._type === "link" && def.href) {
      const isExternal = def.href.startsWith("http") && !def.href.startsWith(SITE_URL);
      node = (
        <a
          key={mark}
          href={def.href}
          {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          className="text-pink-soft underline hover:text-white transition-colors"
        >
          {node}
        </a>
      );
    } else if (mark === "strong") {
      node = <strong key={mark}>{node}</strong>;
    } else if (mark === "em") {
      node = <em key={mark}>{node}</em>;
    } else if (mark === "code") {
      node = (
        <code
          key={mark}
          className="bg-white/10 rounded px-1 py-0.5 font-mono text-sm text-pink-soft"
        >
          {node}
        </code>
      );
    }
  }
  return node;
}

// @sanity/table stores a table as { rows: [{ cells: string[] }] } with plain-text
// cells. By convention the first row is the header. Cells carry no rich text, so
// they render as-is.
function renderTable(table: TableBlock, index: number): React.ReactNode {
  const rows = table.rows ?? [];
  if (rows.length === 0) return null;

  const [headRow, ...bodyRows] = rows;

  return (
    <div key={table._key ?? `table-${index}`} className="my-8 overflow-x-auto">
      <table className="w-full border-collapse text-left text-sm text-white/80">
        <thead>
          <tr className="border-b border-white/20">
            {(headRow.cells ?? []).map((cell, ci) => (
              <th key={ci} className="px-4 py-2 font-semibold text-white">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bodyRows.map((row, ri) => (
            <tr key={row._key ?? ri} className="border-b border-white/10">
              {(row.cells ?? []).map((cell, ci) => (
                <td key={ci} className="px-4 py-2 align-top">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderBlock(block: Block, index: number): React.ReactNode {
  const markDefs = block.markDefs ?? [];
  const children = (block.children ?? []).map((span, i) => (
    <React.Fragment key={i}>{resolveSpanText(span, markDefs)}</React.Fragment>
  ));

  if (block.listItem) {
    return <li key={block._key ?? index}>{children}</li>;
  }

  switch (block.style) {
    case "h2":
      return (
        <h2 key={block._key ?? index} className="text-2xl font-semibold text-white mt-10 mb-4">
          {children}
        </h2>
      );
    case "h3":
      return (
        <h3 key={block._key ?? index} className="text-xl font-semibold text-white mt-8 mb-3">
          {children}
        </h3>
      );
    case "blockquote":
      return (
        <blockquote
          key={block._key ?? index}
          className="border-l-4 border-pink-soft pl-4 my-6 text-white/70 italic"
        >
          {children}
        </blockquote>
      );
    default:
      return (
        <p key={block._key ?? index} className="text-white/80 leading-relaxed mb-4">
          {children}
        </p>
      );
  }
}

export function PortableText({ value, className }: Props): React.ReactElement | null {
  if (!Array.isArray(value) || value.length === 0) return null;

  const nodes = value as PortableTextNode[];
  const output: React.ReactNode[] = [];
  let i = 0;

  while (i < nodes.length) {
    const node = nodes[i];

    if (node._type === "image") {
      const img = node as ImageBlock;
      const src = imageSrc(img);
      if (src) {
        const dimensions = imageDimensions(img.asset?._ref);
        const alt = img.alt && img.alt !== PLACEHOLDER_ALT ? img.alt : "";
        output.push(
          <figure key={node._key ?? i} className="my-8">
            <img
              src={src}
              alt={alt}
              width={dimensions?.width}
              height={dimensions?.height}
              className="w-full h-auto rounded-lg"
              loading="lazy"
              decoding="async"
            />
            {img.caption && (
              <figcaption className="mt-2 text-sm text-white/50 text-center">
                {img.caption}
              </figcaption>
            )}
          </figure>,
        );
      }
      i++;
      continue;
    }

    if (node._type === "table") {
      const rendered = renderTable(node as TableBlock, i);
      if (rendered) output.push(rendered);
      i++;
      continue;
    }

    if (node._type === "block") {
      const block = node as Block;

      if (block.listItem === "bullet") {
        const items: React.ReactNode[] = [];
        while (i < nodes.length) {
          const n = nodes[i] as Block;
          if (n._type === "block" && n.listItem === "bullet") {
            items.push(renderBlock(n, i));
            i++;
          } else {
            break;
          }
        }
        output.push(
          <ul key={`ul-${i}`} className="list-disc list-inside space-y-1 mb-4 text-white/80">
            {items}
          </ul>,
        );
        continue;
      }

      if (block.listItem === "number") {
        const items: React.ReactNode[] = [];
        while (i < nodes.length) {
          const n = nodes[i] as Block;
          if (n._type === "block" && n.listItem === "number") {
            items.push(renderBlock(n, i));
            i++;
          } else {
            break;
          }
        }
        output.push(
          <ol key={`ol-${i}`} className="list-decimal list-inside space-y-1 mb-4 text-white/80">
            {items}
          </ol>,
        );
        continue;
      }

      output.push(renderBlock(block, i));
      i++;
      continue;
    }

    i++;
  }

  return <div className={className ?? "prose-invert max-w-none"}>{output}</div>;
}
